import type { GridApi } from 'ag-grid-community';
import * as R from 'ramda';

import type { BimContractSeasonData, BimWorkOrderSeasonSelectionData } from '../gql/graphql';
import type { AnyObject } from '../types/AnyObject';

import { MIME_TYPES, requiredBaseRule } from './constants';

export * from './formatNumberToRu';
export { formatDateToLocale, getDateOnly } from './formatDateToLocale';

export const isNotNil = R.complement(R.isNil);
export const isNotEmpty = R.complement(R.isEmpty);
export const newDate = R.unless(R.isNil, R.constructN(1, Date));
export const toISOString = R.unless(R.isNil, R.invoker(0, 'toISOString'));
export const toLocaleDateString = R.unless(R.isNil, R.invoker(0, 'toLocaleDateString'));
export const getCheckedIdsArr = R.pipe(R.pickBy(R.identity), R.keys, R.map(Number));
export const getCheckedIdsFromObjValues = (names: string[]) =>
    R.pipe(R.pickBy(R.identity), R.values, R.map(R.pickAll(names)));
export const pickTrue = R.pickBy(R.identity);
export const countTrueValues = R.pipe(pickTrue, R.keys, R.length);

// @ts-ignore
export const mapKeys = R.curry((fn, obj) => R.fromPairs(R.map(R.adjust(0, fn), R.toPairs(obj))));
// eslint-disable-next-line no-underscore-dangle
export const renameKeys = R.curry((keyMap, obj) => mapKeys((key: string) => keyMap[key] || key, obj));

export const isNumber = R.is(Number);

export const isGreaterThanZero = (val: any) => isNumber(val) && val > 0;

export const onlyGreaterThanZero = (obj: any) => R.pickBy(isGreaterThanZero, obj);

export const formatBytes = (bytes: number, decimals = 2) => {
    if (!+bytes) {
        return '0 Байт';
    }

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Байт', 'Кб', 'Мб', 'Гб', 'Тб', 'Пб', 'Еб', 'Зб', 'Иб'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

export const pushIfValue = <T = any>(array: T[], value: T) => {
    if (value) {
        array.push(value);
    }
};

export const yesOrNoRepresentation = (val: any) => (val ? 'Да' : 'Нет');

export const validateInterval = (value: any) => {
    if (!value || value.includes(undefined) || value.includes(null)) {
        return 'Поле обязательно для заполнения';
    }

    return true;
};

export const pickByPaths = (objToProcess: any, paths: Array<Array<number | string>>) =>
    R.reduce(
        (o, p) => {
            const val = R.path(p, objToProcess);

            return typeof val === 'undefined' ? o : R.assocPath(p, val, o);
        },
        {},
        paths,
    );

export const pickProps = (objToProcess: any, paths: Array<Array<number | string> | string | number>) => {
    const processedPaths = paths.map((p) => {
        if (Array.isArray(p)) {
            return p;
        }

        return typeof p === 'number' ? [p] : p.split('.');
    });

    return pickByPaths(objToProcess, processedPaths);
};

export const sortByYear = (
    a: BimContractSeasonData | BimWorkOrderSeasonSelectionData | null,
    b: BimContractSeasonData | BimWorkOrderSeasonSelectionData | null,
) => {
    if (a && b) {
        return a.year - b.year;
    }

    return 0;
};

export const getLengthValidator = (isRequired: boolean, maxValueLength?: number) => ({
    validate: (value: string) => {
        if (isRequired && !value) {
            return requiredBaseRule.required;
        }

        const maxLength = maxValueLength ?? 255;

        if (value?.length > maxLength) {
            return `Не более ${maxLength} символов`;
        }

        return true;
    },
});

export const destroyAgGridTableFilters = ({ api, columns }: { api: GridApi<any> | undefined; columns: string[] }) => {
    if (!api || !columns) {
        return;
    }

    columns.forEach((filterName) => {
        api.destroyFilter?.(filterName);
    });
};

export const extractValuesByKeys = <T extends string>(keys: Array<T>, obj: AnyObject): { [K in T]?: unknown } => {
    const result: { [K in T]?: unknown } = {};
    const extract = (val: AnyObject) => {
        if (R.is(Object, val)) {
            keys.forEach((key) => {
                if (R.has(key, val)) {
                    result[key] = (val as { [key: string]: unknown })[key];
                }
            });
            R.values(val).forEach(extract);
        }
    };

    extract(obj);

    return result;
};

export const checkArraySubset = (array: any[], arrayToCheck: any[], isEvery = true) => {
    const includesPredicate = (el: any) => array.includes(el);

    return isEvery ? arrayToCheck.every(includesPredicate) : arrayToCheck.some(includesPredicate);
};

export const checkObjectHasPathByKey = <T extends Record<string, unknown>>(path: string, obj: T) =>
    R.hasPath(R.split('.', path), obj);

export const capitalize = (value: string) => {
    const lowered = value.toLowerCase();

    return lowered.charAt(0).toUpperCase() + lowered.slice(1);
};

export const getFileExtention = (value: string) => Object.entries(MIME_TYPES).find(([, type]) => type === value)?.[0];
